import type { EChartsOption } from 'echarts'
import type { PublicDemoBundle } from '../types'
import { money, percent, monthLabel } from './format'
import { copyFor, labelFor, type Locale } from '../i18n'

const colors: Record<string, string> = {
  ACTIVE_LIFESTYLE: '#2F9FB3',
  DAILY_WELLNESS: '#5FAF8B',
  HOME_ESSENTIALS: '#D6A84B',
  PERSONAL_CARE: '#7A6FA8',
}

const businessLabel = (value: string, locale: Locale = 'en') => labelFor(value, locale)

export function heroTrendOption(data: PublicDemoBundle['overview']['weekly_performance']): EChartsOption {
  return {
    animationDuration: 900,
    grid: { left: 0, right: 0, top: 10, bottom: 0 },
    xAxis: { type: 'category', boundaryGap: false, data: data.map((d) => d.week_start), show: false },
    yAxis: { type: 'value', show: false, min: 'dataMin' },
    series: [
      {
        type: 'line',
        data: data.map((d) => d.net_sales_after_returns),
        smooth: 0.3,
        symbol: 'none',
        lineStyle: { color: 'rgba(111, 215, 220, .52)', width: 3 },
        areaStyle: { color: 'rgba(47, 159, 179, .08)' },
        silent: true,
      },
      {
        type: 'line',
        data: data.map((d) => d.contribution_profit),
        smooth: 0.3,
        symbol: 'none',
        lineStyle: { color: 'rgba(214, 168, 75, .46)', width: 2 },
        silent: true,
      },
    ],
  }
}

export function executiveTrendOption(data: PublicDemoBundle['overview']['weekly_performance'], compact = false, locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  return {
    animationDuration: 700,
    color: ['#2F9FB3', '#17233C'],
    legend: compact
      ? { top: 0, right: 0, itemWidth: 9, itemHeight: 9, textStyle: { color: '#64748B', fontSize: 10 } }
      : { top: 4, left: 0, itemWidth: 11, itemHeight: 11, textStyle: { color: '#526176', fontSize: 12 } },
    grid: compact
      ? { left: 8, right: 8, top: 35, bottom: 18, containLabel: false }
      : { left: 72, right: 34, top: 54, bottom: 58 },
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#17233C',
      borderWidth: 0,
      textStyle: { color: '#F8FAFC' },
      formatter: (params: unknown) => {
        const rows = params as Array<{ axisValue: string; seriesName: string; value: number; marker: string }>
        return `<strong>${c.weekOf} ${rows[0]?.axisValue ?? ''}</strong><br/>${rows.map((row) => `${row.marker}${row.seriesName}&nbsp;&nbsp;${money(row.value, 1, locale)}`).join('<br/>')}`
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: data.map((d) => d.week_start),
      axisLine: { lineStyle: { color: '#D9E0E8' } },
      axisTick: { show: false },
      axisLabel: compact ? { show: false } : { color: '#7A8899', formatter: (value: string) => value.slice(5) },
    },
    yAxis: {
      type: 'value',
      min: 0,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: compact ? { show: false } : { color: '#7A8899', formatter: (value: number) => money(value, 0, locale) },
      splitLine: { lineStyle: { color: '#E8EDF2' } },
    },
    series: [
      {
        name: c.netSales,
        type: 'line',
        data: data.map((d) => d.net_sales_after_returns),
        smooth: 0.28,
        showSymbol: false,
        lineStyle: { width: compact ? 2.5 : 3 },
        areaStyle: { color: 'rgba(47,159,179,.08)' },
      },
      {
        name: c.contributionProfit,
        type: 'line',
        data: data.map((d) => d.contribution_profit),
        smooth: 0.28,
        showSymbol: false,
        lineStyle: { width: compact ? 2 : 2.5 },
      },
    ],
  }
}

export function productContributionOption(products: PublicDemoBundle['product_portfolio']['products'], direction: 'positive' | 'negative', locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  const selected = products
    .filter((product) => direction === 'positive' ? product.sales_change > 0 : product.sales_change < 0)
    .sort((a, b) => direction === 'positive' ? b.sales_change - a.sales_change : a.sales_change - b.sales_change)
    .slice(0, 5)
    .reverse()
  const color = direction === 'positive' ? '#5FAF8B' : '#D9776A'
  return {
    animationDuration: 600,
    grid: { left: 72, right: 28, top: 8, bottom: 20 },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#17233C',
      borderWidth: 0,
      textStyle: { color: '#F8FAFC' },
      formatter: (params: unknown) => {
        const p = params as { dataIndex: number }
        const product = selected[p.dataIndex]
        return `<strong>${product.product_id}</strong><br/>${businessLabel(product.category, locale)}<br/>${c.salesChange}&nbsp;&nbsp;${money(product.sales_change, 1, locale)}<br/>${c.currentSales}&nbsp;&nbsp;${money(product.current_sales, 1, locale)}`
      },
    },
    xAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#94A3B8', formatter: (value: number) => money(value, 0, locale) },
      splitLine: { lineStyle: { color: '#EDF1F5' } },
    },
    yAxis: {
      type: 'category',
      data: selected.map((product) => product.product_id),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#526176', fontSize: 11 },
    },
    series: [{
      type: 'bar',
      data: selected.map((product) => product.sales_change),
      barWidth: 13,
      itemStyle: { color, borderRadius: direction === 'positive' ? [0, 4, 4, 0] : [4, 0, 0, 4] },
      label: { show: true, position: 'right', color: '#526176', fontSize: 10, formatter: (params: unknown) => money((params as { value: number }).value, 0, locale) },
    }],
  }
}

export function productPortfolioOption(portfolio: PublicDemoBundle['product_portfolio'], locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  const profits = portfolio.products.map((p) => p.gross_profit)
  const minProfit = Math.min(...profits)
  const maxProfit = Math.max(...profits)
  const size = (value: number) => 14 + 34 * Math.sqrt((value - minProfit) / Math.max(maxProfit - minProfit, 1))
  const categories = [...new Set(portfolio.products.map((p) => p.category))]
  return {
    animationDuration: 700,
    color: categories.map((category) => colors[category] ?? '#64748B'),
    legend: { top: 2, left: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: '#475569', fontSize: 12 } },
    grid: { left: 76, right: 76, top: 54, bottom: 58 },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#17233C',
      borderWidth: 0,
      textStyle: { color: '#F8FAFC' },
      formatter: (params: unknown) => {
        const p = params as { data: { value: number[]; product: PublicDemoBundle['product_portfolio']['products'][number] } }
        const d = p.data.product
        return `<strong>${d.product_id}</strong><br/>${businessLabel(d.category, locale)}<br/><br/>${c.sales}&nbsp;&nbsp;${money(d.current_sales, 1, locale)}<br/>${c.grossMargin}&nbsp;&nbsp;${percent(d.gross_margin)}<br/>${c.grossProfit}&nbsp;&nbsp;${money(d.gross_profit, 1, locale)}<br/>${c.units}&nbsp;&nbsp;${d.units.toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-US')}<br/>${c.discountRate}&nbsp;&nbsp;${percent(d.discount_rate)}<br/>${c.salesChange}&nbsp;&nbsp;${money(d.sales_change, 1, locale)}`
      },
    },
    xAxis: {
      type: 'value',
      name: c.currentPeriodSales,
      nameLocation: 'middle',
      nameGap: 38,
      axisLabel: { formatter: (v: number) => money(v, 0, locale), color: '#64748B' },
      nameTextStyle: { color: '#475569', fontWeight: 600 },
      splitLine: { lineStyle: { color: '#E8EDF2' } },
    },
    yAxis: {
      type: 'value',
      name: c.grossMargin,
      nameLocation: 'middle',
      nameGap: 54,
      axisLabel: { formatter: (v: number) => `${v.toFixed(0)}%`, color: '#64748B' },
      nameTextStyle: { color: '#475569', fontWeight: 600 },
      splitLine: { lineStyle: { color: '#E8EDF2' } },
    },
    series: categories.map((category, index) => ({
      name: businessLabel(category, locale),
      type: 'scatter',
      emphasis: { focus: 'series', scale: 1.12 },
      itemStyle: { opacity: 0.82, borderColor: '#FFFFFF', borderWidth: 2 },
      symbolSize: (value: number[]) => size(value[2]),
      data: portfolio.products
        .filter((product) => product.category === category)
        .map((product) => ({ value: [product.current_sales, product.gross_margin * 100, product.gross_profit], product })),
      markLine: index === 0 ? {
        silent: true,
        symbol: ['none', 'none'],
        lineStyle: { color: '#8291A6', type: 'dashed', width: 1 },
        label: { color: '#64748B', fontSize: 11, backgroundColor: '#FFFFFF', padding: [3, 5] },
        data: [
          { xAxis: portfolio.median_sales, label: { formatter: c.medianSales, position: 'insideStartTop' } },
          { yAxis: portfolio.median_gross_margin * 100, label: { formatter: c.medianMargin, position: 'insideEndTop' } },
        ],
      } : undefined,
    })),
    media: [
      {
        query: { maxWidth: 500 },
        option: {
          grid: { left: 54, right: 18, top: 102, bottom: 52 },
          legend: { top: 2, left: 0, right: 0 },
          xAxis: { nameGap: 34 },
          yAxis: { nameGap: 40 },
        },
      },
    ],
  }
}

export function cohortHeatmapOption(cohorts: PublicDemoBundle['customer_cohorts'], locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  const months = cohorts.cohorts.map((cohort) => monthLabel(cohort.cohort_month, locale))
  const ages = cohorts.repeat_ages.map((age) => `M${age}`)
  const available: Array<{ value: [number, number, number]; cell: PublicDemoBundle['customer_cohorts']['cohorts'][number]['ages'][number]; cohort: PublicDemoBundle['customer_cohorts']['cohorts'][number] }> = []
  const unavailable: Array<{ value: [number, number]; cohort: string; age: number }> = []
  cohorts.cohorts.forEach((cohort, y) => cohort.ages.forEach((cell, x) => {
    if (cell.repeat_rate === null) unavailable.push({ value: [x, y], cohort: monthLabel(cohort.cohort_month, locale), age: cell.age })
    else available.push({ value: [x, y, cell.repeat_rate * 100], cell, cohort })
  }))
  const max = Math.max(...available.map((d) => d.value[2]))
  const min = Math.max(0, Math.floor(Math.min(...available.map((d) => d.value[2])) / 5) * 5)
  return {
    animationDuration: 650,
    grid: { left: 76, right: 38, top: 62, bottom: 42 },
    tooltip: {
      backgroundColor: '#17233C', borderWidth: 0, textStyle: { color: '#F8FAFC' },
      formatter: (params: unknown) => {
        const p = params as { seriesName: string; data: typeof available[number] | typeof unavailable[number] }
        if (p.seriesName === copyFor(locale).common.unavailable) {
          const d = p.data as typeof unavailable[number]
          return `<strong>${d.cohort} ${c.cohort} · M${d.age}</strong><br/>${copyFor(locale).common.unavailable}`
        }
        const d = p.data as typeof available[number]
        return `<strong>${monthLabel(d.cohort.cohort_month, locale)} ${c.cohort} · M${d.cell.age}</strong><br/>${c.cohortSize}&nbsp;&nbsp;${d.cohort.cohort_size}<br/>${c.eligible}&nbsp;&nbsp;${d.cell.eligible_customers}<br/>${c.repeatCustomers}&nbsp;&nbsp;${d.cell.repeat_customers}<br/>${c.repeatRate}&nbsp;&nbsp;${percent(d.cell.repeat_rate ?? 0)}`
      },
    },
    xAxis: { type: 'category', data: ages, splitArea: { show: true }, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#475569', fontWeight: 600 } },
    yAxis: { type: 'category', data: months, splitArea: { show: true }, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#475569' } },
    visualMap: {
      min, max, calculable: false, orient: 'horizontal', right: 28, top: 4,
      inRange: { color: ['#ECF7F8', '#A9DBDF', '#2F9FB3', '#205B70', '#17233C'] },
      text: [c.higherRepeat, c.lower], textStyle: { color: '#64748B', fontSize: 11 }, itemWidth: 8, itemHeight: 120,
    },
    series: [
      {
        name: c.repeatRate, type: 'heatmap', data: available,
        label: {
          show: true,
          formatter: (p: unknown) => {
            const value = (p as { value: number[] }).value[2]
            return value >= min + (max - min) * .48 ? `{light|${value.toFixed(0)}%}` : `{dark|${value.toFixed(0)}%}`
          },
          rich: {
            light: { color: '#FFFFFF', fontWeight: 700 },
            dark: { color: '#17243A', fontWeight: 700 },
          },
        },
        itemStyle: { borderColor: '#FFFFFF', borderWidth: 4, borderRadius: 5 },
        emphasis: { itemStyle: { shadowBlur: 12, shadowColor: 'rgba(23,35,60,.22)' } },
      },
      {
        name: copyFor(locale).common.unavailable, type: 'scatter', data: unavailable,
        symbol: 'rect', symbolSize: [1, 1],
        itemStyle: { color: '#EEF1F4' },
        label: { show: true, formatter: 'N/A', color: '#94A3B8', fontSize: 11 },
        tooltip: { show: true },
      },
    ],
  }
}

const tooltipBase = {
  backgroundColor: '#17233C',
  borderWidth: 0,
  textStyle: { color: '#F8FAFC' },
}

export function customerMixOption(rows: PublicDemoBundle['customer_context']['monthly_mix'], locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  return {
    animationDuration: 650,
    color: ['#2F9FB3', '#334155'],
    legend: { top: 2, left: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: '#526176' } },
    grid: { left: 48, right: 20, top: 48, bottom: 42 },
    tooltip: { ...tooltipBase, trigger: 'axis', axisPointer: { type: 'shadow' } },
    xAxis: { type: 'category', data: rows.map((row) => monthLabel(row.activity_month, locale)), axisTick: { show: false }, axisLine: { lineStyle: { color: '#D9E0E8' } }, axisLabel: { color: '#64748B' } },
    yAxis: { type: 'value', axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#64748B' }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    series: [
      { name: c.newCustomers, type: 'bar', stack: 'customers', data: rows.map((row) => row.new_customers), barMaxWidth: 42, itemStyle: { borderRadius: [0, 0, 4, 4] } },
      { name: c.returningCustomers, type: 'bar', stack: 'customers', data: rows.map((row) => row.returning_customers), barMaxWidth: 42, itemStyle: { borderRadius: [4, 4, 0, 0] } },
    ],
  }
}

export function acquisitionQualityOption(rows: PublicDemoBundle['customer_context']['acquisition_sources'], locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  const ordered = [...rows].sort((a, b) => a.repeat_rate - b.repeat_rate)
  return {
    animationDuration: 650,
    grid: { left: 118, right: 38, top: 14, bottom: 34 },
    tooltip: {
      ...tooltipBase,
      trigger: 'item',
      formatter: (params: unknown) => {
        const row = ordered[(params as { dataIndex: number }).dataIndex]
        return `<strong>${businessLabel(row.acquisition_channel, locale)}</strong><br/>${c.acquiredCustomers}&nbsp;&nbsp;${row.acquired_customers}<br/>${c.repeatRate}&nbsp;&nbsp;${percent(row.repeat_rate)}<br/>${c.purchaseFrequency}&nbsp;&nbsp;${row.average_completed_purchases.toFixed(2)}<br/>${c.historicalValue}&nbsp;&nbsp;${money(row.historical_realized_ltv, 1, locale)}`
      },
    },
    xAxis: { type: 'value', max: 1, axisLabel: { formatter: (value: number) => percent(value, 0), color: '#64748B' }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    yAxis: { type: 'category', data: ordered.map((row) => businessLabel(row.acquisition_channel, locale)), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#475569' } },
    series: [{ type: 'bar', data: ordered.map((row) => row.repeat_rate), barWidth: 16, itemStyle: { color: '#2F9FB3', borderRadius: [0, 5, 5, 0] }, label: { show: true, position: 'right', color: '#526176', formatter: (p: unknown) => percent((p as { value: number }).value) } }],
  }
}

const channelColors: Record<string, string> = {
  PAID_SEARCH: '#2F9FB3',
  PAID_SOCIAL: '#7A6FA8',
  MARKETPLACE_ADS: '#D6A84B',
}

export function marketingEfficiencyOption(marketing: PublicDemoBundle['domain_previews']['marketing'], locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  const campaigns = marketing.campaigns
  const spendMedian = [...campaigns].sort((a, b) => a.spend - b.spend)[Math.floor(campaigns.length / 2)].spend
  const roasMedian = [...campaigns].sort((a, b) => a.roas - b.roas)[Math.floor(campaigns.length / 2)].roas
  const revenues = campaigns.map((campaign) => campaign.attributed_revenue)
  const minRevenue = Math.min(...revenues)
  const maxRevenue = Math.max(...revenues)
  const channels = [...new Set(campaigns.map((campaign) => campaign.channel))]
  const bubbleSize = (value: number) => 15 + 31 * Math.sqrt((value - minRevenue) / Math.max(maxRevenue - minRevenue, 1))
  return {
    animationDuration: 700,
    color: channels.map((channel) => channelColors[channel] ?? '#64748B'),
    legend: { top: 2, left: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: '#475569' } },
    grid: { left: 72, right: 58, top: 54, bottom: 58 },
    tooltip: {
      ...tooltipBase,
      formatter: (params: unknown) => {
        const campaign = (params as { data: { campaign: typeof campaigns[number] } }).data.campaign
        return `<strong>${campaign.campaign_name}</strong><br/>${businessLabel(campaign.channel, locale)}<br/><br/>${c.spend}&nbsp;&nbsp;${money(campaign.spend, 1, locale)}<br/>${c.attributedRevenue}&nbsp;&nbsp;${money(campaign.attributed_revenue, 1, locale)}<br/>ROAS&nbsp;&nbsp;${campaign.roas.toFixed(2)}×<br/>CAC&nbsp;&nbsp;${money(campaign.cac, 1, locale)}<br/>${c.acquiredCustomers}&nbsp;&nbsp;${campaign.acquired_customers}`
      },
    },
    xAxis: { type: 'value', name: c.paidSpend, nameLocation: 'middle', nameGap: 38, axisLabel: { formatter: (v: number) => money(v, 0, locale), color: '#64748B' }, nameTextStyle: { color: '#475569', fontWeight: 600 }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    yAxis: { type: 'value', name: 'ROAS', nameLocation: 'middle', nameGap: 46, axisLabel: { formatter: (v: number) => `${v.toFixed(0)}×`, color: '#64748B' }, nameTextStyle: { color: '#475569', fontWeight: 600 }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    series: channels.map((channel, index) => ({
      name: businessLabel(channel, locale), type: 'scatter', itemStyle: { opacity: .84, borderColor: '#FFFFFF', borderWidth: 2 }, emphasis: { focus: 'series', scale: 1.12 },
      symbolSize: (value: number[]) => bubbleSize(value[2]),
      data: campaigns.filter((campaign) => campaign.channel === channel).map((campaign) => ({ value: [campaign.spend, campaign.roas, campaign.attributed_revenue], campaign })),
      markLine: index === 0 ? { silent: true, symbol: ['none', 'none'], lineStyle: { color: '#8291A6', type: 'dashed' }, label: { color: '#64748B', backgroundColor: '#FFFFFF', padding: [3, 5] }, data: [{ xAxis: spendMedian, label: { formatter: c.medianSpend } }, { yAxis: roasMedian, label: { formatter: c.medianRoas } }] } : undefined,
    })),
  }
}

export function marketingChannelOption(campaigns: PublicDemoBundle['domain_previews']['marketing']['campaigns'], locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  const rows = [...new Set(campaigns.map((campaign) => campaign.channel))].map((channel) => {
    const members = campaigns.filter((campaign) => campaign.channel === channel)
    const spend = members.reduce((sum, campaign) => sum + campaign.spend, 0)
    const revenue = members.reduce((sum, campaign) => sum + campaign.attributed_revenue, 0)
    return { channel, spend, revenue, roas: revenue / spend }
  }).sort((a, b) => a.roas - b.roas)
  return {
    animationDuration: 600, grid: { left: 112, right: 48, top: 14, bottom: 34 },
    tooltip: { ...tooltipBase, trigger: 'item', formatter: (p: unknown) => { const row = rows[(p as { dataIndex: number }).dataIndex]; return `<strong>${businessLabel(row.channel, locale)}</strong><br/>${c.spend}&nbsp;&nbsp;${money(row.spend, 1, locale)}<br/>${c.attributedRevenue}&nbsp;&nbsp;${money(row.revenue, 1, locale)}<br/>ROAS&nbsp;&nbsp;${row.roas.toFixed(2)}×` } },
    xAxis: { type: 'value', axisLabel: { color: '#64748B', formatter: (v: number) => `${v.toFixed(0)}×` }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    yAxis: { type: 'category', data: rows.map((row) => businessLabel(row.channel, locale)), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#475569' } },
    series: [{ type: 'bar', data: rows.map((row) => ({ value: row.roas, itemStyle: { color: channelColors[row.channel] ?? '#2F9FB3' } })), barWidth: 17, label: { show: true, position: 'right', color: '#526176', formatter: (p: unknown) => `${(p as { value: number }).value.toFixed(2)}×` }, itemStyle: { borderRadius: [0, 5, 5, 0] } }],
  }
}

export function inventoryRiskOption(inventory: PublicDemoBundle['domain_previews']['inventory'], locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  const positions = inventory.risk_positions
  const categories = [...new Set(positions.map((position) => position.category))]
  const dosMedian = [...positions].sort((a, b) => a.days_of_supply - b.days_of_supply)[Math.floor(positions.length / 2)].days_of_supply
  const demandMedian = [...positions].sort((a, b) => a.average_daily_demand - b.average_daily_demand)[Math.floor(positions.length / 2)].average_daily_demand
  const gaps = positions.map((position) => position.reorder_gap_units)
  const maxGap = Math.max(...gaps)
  return {
    animationDuration: 700, color: categories.map((category) => colors[category] ?? '#64748B'),
    legend: { top: 2, left: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: '#475569' } }, grid: { left: 74, right: 56, top: 54, bottom: 58 },
    tooltip: { ...tooltipBase, formatter: (p: unknown) => { const row = (p as { data: { position: typeof positions[number] } }).data.position; return `<strong>${row.product_id}</strong> · ${businessLabel(row.location_id, locale)}<br/>${businessLabel(row.category, locale)}<br/><br/>${c.daysSupply}&nbsp;&nbsp;${row.days_of_supply.toFixed(1)}<br/>${c.dailyDemand}&nbsp;&nbsp;${row.average_daily_demand.toFixed(1)}<br/>${c.reorderGap}&nbsp;&nbsp;${row.reorder_gap_units}<br/>${c.onHand}&nbsp;&nbsp;${row.on_hand_units}<br/>${c.openPo}&nbsp;&nbsp;${row.open_po_units}` } },
    xAxis: { type: 'value', name: c.daysSupply, nameLocation: 'middle', nameGap: 38, axisLabel: { color: '#64748B' }, nameTextStyle: { color: '#475569', fontWeight: 600 }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    yAxis: { type: 'value', name: c.dailyDemand, nameLocation: 'middle', nameGap: 48, axisLabel: { color: '#64748B' }, nameTextStyle: { color: '#475569', fontWeight: 600 }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    series: categories.map((category, index) => ({
      name: businessLabel(category, locale), type: 'scatter', itemStyle: { opacity: .82, borderColor: '#FFFFFF', borderWidth: 2 }, emphasis: { focus: 'series', scale: 1.1 },
      symbolSize: (value: number[]) => 13 + 28 * Math.sqrt(value[2] / Math.max(maxGap, 1)),
      data: positions.filter((position) => position.category === category).map((position) => ({ value: [position.days_of_supply, position.average_daily_demand, position.reorder_gap_units], position })),
      markLine: index === 0 ? { silent: true, symbol: ['none', 'none'], lineStyle: { color: '#8291A6', type: 'dashed' }, label: { color: '#64748B', backgroundColor: '#FFFFFF', padding: [3, 5] }, data: [{ xAxis: dosMedian, label: { formatter: c.medianCoverage } }, { yAxis: demandMedian, label: { formatter: c.medianDemand } }] } : undefined,
    })),
  }
}

export function deliveryReliabilityOption(operations: PublicDemoBundle['domain_previews']['operations'], locale: Locale = 'en'): EChartsOption {
  const c = copyFor(locale).charts
  const services = operations.services
  const carriers = [...new Set(services.map((service) => service.carrier))]
  const volumeMedian = [...services].sort((a, b) => a.delivered_orders - b.delivered_orders)[Math.floor(services.length / 2)].delivered_orders
  const reliabilityMedian = [...services].sort((a, b) => a.on_time_rate - b.on_time_rate)[Math.floor(services.length / 2)].on_time_rate * 100
  const carrierPalette = ['#2F9FB3', '#334155', '#D6A84B', '#7A6FA8']
  return {
    animationDuration: 700, color: carriers.map((_, index) => carrierPalette[index % carrierPalette.length]),
    legend: { top: 2, left: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: '#475569' } }, grid: { left: 72, right: 58, top: 54, bottom: 58 },
    tooltip: { ...tooltipBase, formatter: (p: unknown) => { const row = (p as { data: { service: typeof services[number] } }).data.service; return `<strong>${businessLabel(row.carrier, locale)} · ${businessLabel(row.service_level, locale)}</strong><br/><br/>${c.delivered}&nbsp;&nbsp;${row.delivered_orders}<br/>${c.onTime}&nbsp;&nbsp;${percent(row.on_time_rate)}<br/>${c.lateOrders}&nbsp;&nbsp;${row.late_deliveries}<br/>${c.averageDelivery}&nbsp;&nbsp;${row.average_delivery_hours.toFixed(1)} ${c.hours}<br/>${c.returnContext}&nbsp;&nbsp;${percent(row.return_context_rate)}` } },
    xAxis: { type: 'value', name: c.deliveredOrders, nameLocation: 'middle', nameGap: 38, axisLabel: { color: '#64748B' }, nameTextStyle: { color: '#475569', fontWeight: 600 }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    yAxis: { type: 'value', name: c.onTimeDelivery, min: 50, max: 100, nameLocation: 'middle', nameGap: 48, axisLabel: { formatter: (v: number) => `${v}%`, color: '#64748B' }, nameTextStyle: { color: '#475569', fontWeight: 600 }, splitLine: { lineStyle: { color: '#E8EDF2' } } },
    series: carriers.map((carrier, index) => ({
      name: businessLabel(carrier, locale), type: 'scatter', itemStyle: { opacity: .84, borderColor: '#FFFFFF', borderWidth: 2 }, emphasis: { focus: 'series', scale: 1.12 },
      symbolSize: 18,
      data: services.filter((service) => service.carrier === carrier).map((service) => ({ value: [service.delivered_orders, service.on_time_rate * 100, service.late_deliveries], service })),
      markLine: index === 0 ? { silent: true, symbol: ['none', 'none'], lineStyle: { color: '#8291A6', type: 'dashed' }, label: { color: '#64748B', backgroundColor: '#FFFFFF', padding: [3, 5] }, data: [{ xAxis: volumeMedian, label: { formatter: c.medianVolume } }, { yAxis: reliabilityMedian, label: { formatter: c.medianReliability } }] } : undefined,
    })),
  }
}
